import { api } from "./api";
import { getBaseUrl } from "./utils";

export type CmsSubmissionPackageStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed";

export type CmsMissingSection = {
  section: string;
  label: string;
};

export type CmsSkippedDocument = {
  document_id: number | null;
  filename: string;
  reason: string;
};

export type CmsSubmissionWarnings = {
  missing_sections: CmsMissingSection[];
  skipped_documents: CmsSkippedDocument[];
  warnings: string[];
};

export type CmsSubmissionPlanItem = {
  position: number;
  section: string;
  filename: string;
  source_document: number | null;
  source_filename: string | null;
  treatment_year: number | null;
  record_group_count: number;
};

export type CmsUndatedRecord = {
  id: number;
  start_page: number;
  end_page: number;
  provider: string | null;
  document_type: string;
};

export type CmsNonPdfDocument = {
  document_id: number;
  filename: string;
  extension: string | null;
};

export type CmsSubmissionPlan = CmsSubmissionWarnings & {
  claimant: string;
  metadoc: number | null;
  items: CmsSubmissionPlanItem[];
  undated_records: CmsUndatedRecord[];
  non_pdf_documents: CmsNonPdfDocument[];
};

export type CmsSubmissionPackageItem = {
  id: number;
  position: number;
  section: string;
  filename: string;
  source_document: number | null;
  treatment_year: number | null;
  page_count: number;
};

export type CmsSubmissionPackage = {
  id: number;
  file: number;
  package_version: number;
  status: CmsSubmissionPackageStatus;
  created: string;
  completed_at: string | null;
  created_by: number;
  created_by_name: string;
  // Pending and failed packages have no warnings yet.
  warnings: Partial<CmsSubmissionWarnings>;
  error_message: string | null;
  items: CmsSubmissionPackageItem[];
};

const PACKAGES_URL = "api/v1/cms-submission-packages/";

export const ACTIVE_CMS_SUBMISSION_PACKAGE_STATUSES: readonly CmsSubmissionPackageStatus[] =
  ["pending", "processing"];

const CMS_SUBMISSION_PACKAGE_STALE_AFTER_MS = 61 * 60 * 1000;

export const isCmsPackageActive = (pkg: Pick<CmsSubmissionPackage, "status">) =>
  ACTIVE_CMS_SUBMISSION_PACKAGE_STATUSES.includes(pkg.status);

export const isCmsPackageStale = (
  pkg: Pick<CmsSubmissionPackage, "status" | "created">,
  now = Date.now(),
) =>
  isCmsPackageActive(pkg) &&
  now - new Date(pkg.created).getTime() >=
    CMS_SUBMISSION_PACKAGE_STALE_AFTER_MS;

export const getCmsPackageZipUrl = (packageId: number) =>
  new URL(`${PACKAGES_URL}${packageId}/download/`, getBaseUrl()).toString();

export const getCmsPackageItemUrl = (packageId: number, itemId: number) =>
  new URL(
    `${PACKAGES_URL}${packageId}/items/${itemId}/download/`,
    getBaseUrl(),
  ).toString();

const cmsSubmissionApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCmsSubmissionPlan: build.query<CmsSubmissionPlan, { fileId: number }>({
      query: ({ fileId }) => ({
        url: `${PACKAGES_URL}preview/`,
        params: { file: fileId },
      }),
      providesTags: ["CmsSubmissionPackages"],
    }),
    getCmsSubmissionPackages: build.query<
      { results: CmsSubmissionPackage[] },
      { fileId: number }
    >({
      query: ({ fileId }) => ({
        url: PACKAGES_URL,
        params: { file: fileId },
      }),
      providesTags: ["CmsSubmissionPackages"],
    }),
    createCmsSubmissionPackage: build.mutation<
      CmsSubmissionPackage,
      { fileId: number }
    >({
      query: ({ fileId }) => ({
        url: PACKAGES_URL,
        method: "POST",
        body: { file: fileId },
      }),
      invalidatesTags: ["CmsSubmissionPackages"],
    }),
  }),
});

export const {
  useGetCmsSubmissionPlanQuery,
  useGetCmsSubmissionPackagesQuery,
  useCreateCmsSubmissionPackageMutation,
} = cmsSubmissionApi;

export default cmsSubmissionApi;
